// "no-unknown-property" is broken so disabling it...
/* eslint-disable react/no-unknown-property */
// ESlint doesn't understand what a regex is
/* eslint-disable no-useless-escape */
import React, { useState } from 'react'
import { makeStyles, Checkbox } from '@material-ui/core'
import GlueButton from '../glueos-components/glue-button'
import GlueDropdown from '../glueos-components/glue-dropdown'
import InputField from './input-field'
import InputValidator from './input-validator'
import { queries, mutations } from '../../graphql'
import { useQuery, useMutation } from '@apollo/react-hooks'
import ErrorDialog from './error-dialog'
import LoadingIndicator from '../glueos-components/loading-indicator'
import OrgCreatedDialog from './org-created-dialog'
import PolicyDialog from './policy-dialog'
import PrivacyPolicy from './privacy-policy'
import GeneralTermsAndConditions from './general-terms-and-conditions'
import LoginPageTemplate from './login-page-template'

const useStyles = makeStyles(theme => ({
  createOrgInfo: {
    display: 'flex',
    flexFlow: 'column'
  },

  inputs: {
    display: 'flex',
    flexFlow: 'column',
    paddingTop: theme.spacing('l'),
    paddingBottom: theme.spacing('xl')
  },

  regionDropdown: {
    display: 'flex',
    flexFlow: 'column',
    gap: theme.spacing('xs')
  },

  createOrgForm: {
    display: 'flex',
    gap: theme.spacing('s'),
    flexFlow: 'column'
  },

  createOrgBox: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },

  createOrgContent: {
    width: '1110px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  policyLinks: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    width: '544px',
    paddingBottom: '76px'
  },

  policyCheck: {
    display: 'flex',
    gap: theme.spacing('s'),
    alignItems: 'center',

    '&> p': {
      opacity: '60%'
    }
  },

  smallPrint: {
    '&> p': {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '12px',
      letterSpacing: '0.02em'
    }
  }
}), { name: 'MuiGlueCreateOrganization' })

const isOrgNameValid = (value) => {
  if (!value.trim()) {
    return { success: false, msg: 'Name is empty' }
  } else if (value.length > 30) {
    return { success: false, msg: 'Max. 30 characters' }
  } else {
    return { success: true }
  }
}

const isEmailValid = (value) => {
  const regExp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  if (!regExp.test(value)) {
    return { success: false, msg: 'Invalid email' }
  } else if (!/^[ -~]+$/.test(value)) {
    return { success: false, msg: 'Ascii characters only' }
  } else {
    return { success: true }
  }
}

const isPasswordValid = (value) => {
  let hasLowercase = /^(?=.*[a-z])/
  let hasUppercase = /^(?=.*[A-Z])/
  let hasSpecialCharacter = /^(?=.*[ !"#$%&\'()*+,\-./:;<=>?@[\\\]^_`{|}~])/

  if (value.length < 10 || !value.match(hasLowercase) || !value.match(hasUppercase) || !value.match(hasSpecialCharacter)) {
    return { success: false, msg: 'Weak password' }
  } else {
    return { success: true }
  }
}

const CreateOrganization = (props) => {
  const orgCreationRedirectUrl = props.web ? 'https://glue.work/get-started-download-glue/' : (window.location.protocol + '//' + window.location.host)
  const classes = useStyles()

  const [ selectedRegionId, setSelectedRegionId ] = useState(null)
  const [ orgName, setOrgName ] = useState(null)
  const [ email, setEmail ] = useState(null)
  const [ password, setPassword ] = useState(null)
  const [ password2, setPassword2 ] = useState(null)
  const [ policyAccepted, setPolicyAccepted ] = useState(false)
  const [ emailError, setEmailError ] = useState(null)
  const [ dialogError, setDialogError ] = useState(null)
  const [ loading, setLoading ] = useState(false)
  const [ successDialogState, setSuccessDialogState ] = useState(false)
  const [ policyOpen, setPolicyOpen ] = useState(false)
  const [ termsConditionsOpen, setTermsConditionsOpen ] = useState(false)

  const clustersRes = useQuery(queries.publicClusters, {
    fetchPolicy: 'network-only',
    name: 'publicClusters'
  })

  const [ createOrg ] = useMutation(mutations.createTrialTeam)

  let regions = clustersRes.data?.publicClusters?.map(e => { return { id: e.clusterId, name: e.name } }) ?? []
  const defaultRegionId = regions.find(r => r.name === 'Europe')?.id

  regions = regions.sort(function (a, b) {
    a = a.name
    b = b.name
    return a?.toLowerCase().localeCompare(b?.toLowerCase())
  })

  const create = () => {
    if (loading || !canCreateOrg()) {
      return
    }

    setLoading(true)
    createOrg({
      variables: {
        email: email,
        password: password,
        teamName: orgName,
        region: selectedRegionId ?? defaultRegionId,
        webOrigin: window.location.origin
      },
      refetchQueries: [
      ]
    }).then((res) => {
      setLoading(false)
      // Error handling
      switch (res.data?.createTrialTeam?.responseCode) {
        case 1:
          // Success!!
          if (window.analytics) {
            if (props.web) {
              window.analytics.logEvent('team_created_from_web', { name: 'team_created_from_web' })
            } else {
              window.analytics.logEvent('team_created_from_app', { name: 'team_created_from_app' })
            }
          }

          // Effectively logging in the newly created user...
          window.localStorage.setItem('idToken', res.data.createTrialTeam.authToken)

          // TODO: When this is moved to GlueOS, use prompt dialog and give this data to it directly instead of abusing state like this...
          console.log(res.data)
          setSuccessDialogState({ open: true, orgId: res.data.createTrialTeam.orgId, orgName: res.data.createTrialTeam.name, email: email })
          break
        case -1:
          // Glue service may be down at the moment
          setDialogError({ header: 'Connection issue', body: <p>Glue service may be down at the moment.<br />Please try again after a while.</p> })
          break
        case -2:
          // This is an email validation failure from our own backend checks
          setEmailError('Invalid email address')
          break
        case -3:
          // Can't create org with user that already exists...
          setEmailError('User already exists')
          break
        case -4:
          // This is an email validation failure from auth0
          setEmailError('Invalid email address')
          break
        default:
          setDialogError({ header: 'Unknown error', body: <p>We have to be honest, we don't know what went wrong this time.<br />Please try again and if that doesn't resolve the problem, contact Glue support.</p> })
          break
      }
    })
  }

  const orgNameValid = !!orgName && isOrgNameValid(orgName)
  const emailValid = !!email && isEmailValid(email)
  const passwordValid = !!password && isPasswordValid(password)

  const canCreateOrg = () => !!orgNameValid && orgNameValid.success && !!emailValid && emailValid.success && !!passwordValid && passwordValid.success && !!policyAccepted && password === password2

  const createOrgInfo = (
    <div className={classes.createOrgInfo}>
      <div className={classes.inputs}>
        <h1>Create a new organization</h1>
        <br />
        {props.web ? (
          <b>By completing this form, you&apos;ll create a new organization in Glue and become its first admin.</b>
        ) : (
          <p>By completing this form, you&apos;ll create a new organization in Glue and become its first admin.</p>
        )}
        <br />
        <p>As an admin, you can invite others to join the organization and create teams and spaces where team members can meet.</p>
      </div>
    </div>
  )

  const createOrgForm = (
    <div className={classes.createOrgForm}>
      <div className={classes.regionDropdown}>
        <p>Server Region</p>
        <GlueDropdown label={clustersRes.loading && 'Loading...'} value={selectedRegionId ?? defaultRegionId} items={regions} onChange={(id, checked) => setSelectedRegionId(id)} />
      </div>
      <InputValidator valid={!!orgNameValid && orgNameValid.success}>
        <InputField width='100%' multiline label='Organization name' value={orgName} onChange={setOrgName} error={!!orgNameValid && !orgNameValid.success && orgNameValid.msg} />
      </InputValidator>
      <InputValidator valid={!!emailValid && emailValid.success}>
        <InputField width='100%' label='Admin email' type='email' value={email} onChange={(value) => { setEmail(value); setEmailError(null) }} error={!!emailValid && !emailValid.success ? emailValid.msg : emailError} />
      </InputValidator>
      <InputValidator valid={!!passwordValid && passwordValid.success}>
        <InputField width='100%' label='Password' type='password' value={password} onChange={setPassword} info='Min. 10 characters, at least one upper & lowercase letter and special character (e.g. !@#$%^&*)' error={!!passwordValid && !passwordValid.success && passwordValid.msg} />
      </InputValidator>
      <InputValidator valid={!!passwordValid && passwordValid.success && (password === password2)}>
        <InputField width='100%' label='Repeat password' type='password' value={password2} onChange={setPassword2} info='Type your new password again' error={password !== password2} onSubmitKey={create} />
      </InputValidator>
      <div className={classes.policyCheck}>
        <Checkbox checked={policyAccepted} onChange={(e) => setPolicyAccepted(e.target.checked)} />
        { props.web ? (
          <p>I accept the <a href='https://www.glue.work/glue-privacy-policy/' target='_blank'>Glue Privacy Policy</a> and <a href='https://glue.work/terms-and-conditions/' target='_blank'>General Terms and Conditions.</a></p>
        ) : (
          <p>I accept the Glue Privacy Policy and General Terms and Conditions.</p>
        )}
      </div>
      {props.web && (
        <div className={classes.smallPrint}>
          <p>The user account with the given email will become the organization&apos;s first administrator. You may add more users and administrators later.</p>
        </div>
      )}
    </div>
  )

  const createOrgBox = (
    <div className={classes.createOrgBox}>
      <div className={classes.createOrgContent}>
        <div className={classes.policyLinks}>
          <GlueButton color='link' onPointerDown={() => setPolicyOpen(true)}>
            Privacy Policy
          </GlueButton>
          <GlueButton color='link' onPointerDown={() => setTermsConditionsOpen(true)}>
            General Terms and Conditions
          </GlueButton>
        </div>
        <GlueButton width='432px' color='primary' disabled={!canCreateOrg()} onPointerDown={create}>
          { loading ? (
            <LoadingIndicator variant='button' />
          ) : (
            <p>Create this organization</p>
          )}
        </GlueButton>
      </div>
    </div>
  )

  const createOrgBoxWeb = (
    <div className={classes.createOrgBox}>
      <GlueButton width='260px' color='primary' disabled={!canCreateOrg()} onPointerDown={create}>
        { loading ? (
          <LoadingIndicator variant='button' />
        ) : (
          <p>Create this organization</p>
        )}
      </GlueButton>
    </div>
  )

  return (
    <>
      <LoginPageTemplate
        canGoBack={!props.web}
        leftSideContent={createOrgInfo}
        rightSideContent={createOrgForm}
        bottomContent={props.web ? createOrgBoxWeb : createOrgBox}
        hideLogo={props.web}
      />
      <ErrorDialog open={!!dialogError} header={dialogError?.header} body={dialogError?.body} onCloseDialog={() => setDialogError(null)} />
      <OrgCreatedDialog
        open={successDialogState && successDialogState.open}
        orgId={successDialogState && successDialogState.orgId}
        orgName={successDialogState && successDialogState.orgName}
        email={successDialogState && successDialogState.email}
        onCloseDialog={() => { window.parent.location.href = orgCreationRedirectUrl }}
      />
      {!props.web && (
        <>
          <PolicyDialog title='Glue Privacy Policy' open={policyOpen} onCloseDialog={() => setPolicyOpen(false)}>
            <PrivacyPolicy />
          </PolicyDialog>
          <PolicyDialog title='General Terms and Conditions' open={termsConditionsOpen} onCloseDialog={() => setTermsConditionsOpen(false)}>
            <GeneralTermsAndConditions />
          </PolicyDialog>
        </>
      )}
    </>
  )
}

export default CreateOrganization
